import { APP_VERSION } from '@/constants/app-version';
import * as Sentry from '@sentry/react';
import { lazy } from 'react';

const CHUNK_LOAD_STORAGE_PREFIX = 'piloteer:chunk-load-retry';
const CHUNK_LOAD_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Failed to load module script/i,
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i,
];

const getErrorMessage = (error: unknown) => {
  if (typeof error === 'string') return error;
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return '';
};

const extractAssetUrl = (value: string) => {
  const match = value.match(/https?:\/\/[^\s)'"]+\/assets\/[^\s)'"]+\.js/);
  return match?.[0] ?? null;
};

const getChunkIdentity = (error: unknown) => {
  const message = getErrorMessage(error);
  const assetUrl = extractAssetUrl(message);

  if (assetUrl) return assetUrl;
  if (message) return message;

  return `${APP_VERSION}:${window.location.pathname}`;
};

export const isChunkLoadError = (error: unknown) => {
  const message = getErrorMessage(error);

  return CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(message));
};

export const recoverFromChunkLoadError = (error: unknown, context?: string) => {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) return false;

  const identity = getChunkIdentity(error);
  const storageKey = `${CHUNK_LOAD_STORAGE_PREFIX}:${identity}`;

  if (window.sessionStorage.getItem(storageKey)) {
    return false;
  }

  window.sessionStorage.setItem(
    storageKey,
    JSON.stringify({
      context,
      appVersion: APP_VERSION,
      pathname: window.location.pathname,
      at: new Date().toISOString(),
    })
  );

  Sentry.addBreadcrumb({
    category: 'chunk-load',
    level: 'warning',
    message: 'Reloading page after chunk load failure',
    data: {
      appVersion: APP_VERSION,
      context,
      identity,
      pathname: window.location.pathname,
    },
  });

  window.location.reload();
  return true;
};

export const lazyWithChunkRecovery: typeof lazy = (importer) =>
  lazy(() =>
    importer().catch((error) => {
      if (recoverFromChunkLoadError(error, 'react-lazy-import')) {
        return new Promise<never>(() => undefined);
      }

      throw error;
    })
  );

export const registerChunkLoadRecovery = () => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleVitePreloadError = (event: Event) => {
    const preloadEvent = event as Event & {
      payload?: unknown;
      detail?: {
        payload?: unknown;
        error?: unknown;
        reason?: unknown;
      };
      preventDefault: () => void;
    };
    const error =
      preloadEvent.payload ??
      preloadEvent.detail?.payload ??
      preloadEvent.detail?.error ??
      preloadEvent.detail?.reason ??
      preloadEvent.detail;

    // Do not preventDefault here. Vite treats a prevented preload error as a
    // resolved dynamic import, which makes React.lazy try to read module.default
    // from undefined before the reload completes.
    recoverFromChunkLoadError(error, 'vite-preload-error');
  };

  window.addEventListener(
    'vite:preloadError',
    handleVitePreloadError as EventListener
  );

  return () => {
    window.removeEventListener(
      'vite:preloadError',
      handleVitePreloadError as EventListener
    );
  };
};
